import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useStore } from 'zustand'
import type { StoreApi } from 'zustand/vanilla'
import { snippetNodeId } from '@/features/source-snippets/object-set'
import {
  getOrCreateInvestigationsStore,
  type InvestigationsState,
} from '@/stores/investigations-store'
import type { ChatToolContext } from './chat-tool-actions'

/**
 * Resolves the chat-tool context for the current render. Reads via the
 * imperative store cache (not the React provider) so tool cards work in
 * surfaces that don't mount `<InvestigationsStoreProvider>` — e.g.
 * `FindingCard` outside CaseLayout. Returns null when there's no case in
 * scope (callers should hide actions in that state).
 */
export function useChatToolContext(): ChatToolContext | null {
  const { caseId } = useParams<{ caseId: string }>()
  const location = useLocation()

  // useStore needs a non-null store; create a no-op fallback when there is
  // no caseId so the hook call stays unconditional.
  const store = useMemo(
    () => (caseId ? getOrCreateInvestigationsStore({ caseId }) : null),
    [caseId]
  )

  // Atomic selectors — each returns a stable reference so zustand's snapshot
  // equality holds across renders. A combined `s => ({...})` selector would
  // produce a fresh object every call and loop the store subscription.
  const activeStore = store ?? FALLBACK_STORE
  const activeId = useStore(activeStore, s => s.activeId)
  const transientView = useStore(activeStore, s => s.transient?.graphView)
  const activeView = useStore(activeStore, s =>
    s.activeId ? (s.saved.find(i => i.id === s.activeId)?.graphView ?? null) : null
  )

  return useMemo<ChatToolContext | null>(() => {
    if (!caseId || !store) return null

    const isOnGraphRoute = location.pathname.includes('/graph')
    const mode: ChatToolContext['mode'] = isOnGraphRoute ? 'sidebar' : 'fullscreen-agent'

    const view = transientView ?? activeView ?? null
    const hasActiveView = !!view
    const loadedEntityIds: ReadonlySet<string> = view
      ? new Set([
          ...view.nodes.map(n => n.id),
          ...(view.snippets ?? []).map(s => snippetNodeId(s.id)),
        ])
      : new Set<string>()

    return {
      mode,
      caseId,
      activeInvestigationId: activeId,
      hasActiveView,
      loadedEntityIds,
    }
  }, [caseId, store, location.pathname, activeId, transientView, activeView])
}

// Empty store used only to keep the `useStore` hook call unconditional when
// no caseId is in scope. The selector reads `activeId`, `transient` and
// `saved`, so the inert state only needs to carry those slots.
const FALLBACK_STATE = {
  activeId: null,
  transient: null,
  saved: [],
} as unknown as InvestigationsState

const FALLBACK_STORE: StoreApi<InvestigationsState> = {
  getState: () => FALLBACK_STATE,
  getInitialState: () => FALLBACK_STATE,
  setState: () => {},
  subscribe: () => () => {},
}
